import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Invoice, AuditLog, AuditAction } from '../../types';

interface InvoiceHistoryDialogProps {
  open: boolean;
  invoice: Invoice | null;
  auditLogs: AuditLog[];
  loading: boolean;
  onOpenChange: (open: boolean) => void;
}

const getActionBadgeVariant = (action: string): 'default' | 'secondary' | 'destructive' => {
  switch (action) {
    case AuditAction.CREATED:
      return 'default';
    case AuditAction.STATUS_CHANGED:
    case AuditAction.NOTES_UPDATED:
    case AuditAction.REVIEWER_COMMENTS_UPDATED:
      return 'secondary';
    case AuditAction.DELETED:
      return 'destructive';
    default:
      return 'default';
  }
};

const getActionDisplayLabel = (action: string): string => {
  switch (action) {
    case AuditAction.CREATED:
      return 'Created';
    case AuditAction.STATUS_CHANGED:
      return 'Status Changed';
    case AuditAction.NOTES_UPDATED:
      return 'Notes Updated';
    case AuditAction.REVIEWER_COMMENTS_UPDATED:
      return 'Reviewer Comments Updated';
    case AuditAction.DELETED:
      return 'Deleted';
    default:
      return action;
  }
};

export const InvoiceHistoryDialog: React.FC<InvoiceHistoryDialogProps> = ({
  open,
  invoice,
  auditLogs,
  loading,
  onOpenChange,
}) => {
  const { t } = useTranslation('invoices');
  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('history.title')}</DialogTitle>
          <DialogDescription>
            {t('history.auditLogFor')}: {invoice.invoiceMonth} - {invoice.userName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : auditLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('history.noHistory')}</p>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 space-y-2 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <Badge variant={getActionBadgeVariant(log.action)}>
                      {getActionDisplayLabel(log.action)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>

                  <div className="text-sm">
                    <p className="font-medium">{log.userName}</p>
                    {log.userEmail && (
                      <p className="text-muted-foreground">{log.userEmail}</p>
                    )}
                  </div>

                  {log.details && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground">{log.details}</p>
                    </div>
                  )}

                  {(log.oldValue || log.newValue) && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium">{t('history.changes')}:</p>
                      <div className="bg-muted rounded p-2 text-xs font-mono">
                        {log.oldValue && (
                          <div>
                            <span className="text-muted-foreground">{t('history.from')}: </span>
                            <span className="text-red-600">{log.oldValue}</span>
                          </div>
                        )}
                        {log.newValue && (
                          <div>
                            <span className="text-muted-foreground">{t('history.to')}: </span>
                            <span className="text-green-600">{log.newValue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
